import Icon from 'src/components/ui/Icon'
import Button from 'src/components/ui/Button'
import { useMobile } from 'src/hooks/useMobile'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-use'

import type { Toast } from './ToastContext'
import { useToastContext } from './ToastContext'

interface DisplayToastsProps {
  toasts: Toast[]
}

export const DisplayToasts = ({ toasts }: DisplayToastsProps) => {
  const { isMobile } = useMobile()
  const [toastTop, setToastTop] = useState(176)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isCheckout, setIsCheckout] = useState(false)
  const { pathname } = useLocation()
  const { removeToast } = useToastContext()

  useEffect(() => {
    let ticking = false

    function handleScroll() {
      if (!ticking && !isMobile) {
        window.requestAnimationFrame(() => {
          const { scrollY } = window

          setToastTop(scrollY > 96 ? 16 : 176)
          setHasScrolled(scrollY > 0)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMobile])

  const reminderTopValue = hasScrolled ? 70 : 230

  useEffect(() => {
    setIsCheckout(pathname?.startsWith('/checkout') ?? false)
  }, [pathname])

  if (!toasts?.length) {
    return null
  }

  const getContainerClass = (variant: string) =>
    `flex items-center  drop-shadow-md ${variant}
    ${
      variant === 'abandoned' || variant === 'pixPaymentReminder'
        ? 'w-full p-[12px] rounded-lg bg-[#2B2B2B]'
        : 'bg-white p-4 rounded-sm'
    }
    ${
      variant === 'pixPaymentReminder'
        ? 'md:max-h-[304px] justify-center md:max-w-[167px]'
        : 'justify-between'
    }
    ${variant === 'abandoned' ? 'h-[72px]' : ''}
    `

  const isReminderToast = (variant: string) =>
    variant === 'abandoned' || variant === 'pixPaymentReminder'

  const visibleToasts = toasts.filter(
    (toast) => !(toast.variant === 'abandoned' && isCheckout)
  )

  const getTextClass = (variant: string, hasSubtext: boolean) => {
    const base =
      variant === 'abandoned' || variant === 'pixPaymentReminder'
        ? 'text-white font-medium text-base leading-6 pb-[10px]'
        : variant === 'success'
        ? 'text-[#101010]'
        : 'text-[#E3262F]'

    return `${base} ${hasSubtext ? 'font-bold' : ''}`
  }

  const renderIconOrImage = (
    variant: string,
    image?: string,
    subtext?: string | ReactNode
  ) =>
    image ? (
      <img
        src={image}
        alt={typeof subtext === 'string' ? subtext : ''}
        className="w-12 h-12 rounded-md object-cover"
      />
    ) : (
      <Icon
        name={variant === 'success' ? 'SuccessCheck' : 'ErrorWarning'}
        width={20}
        height={20}
        className="!overflow-visible"
      />
    )

  const renderCloseButton = (toast: Toast) => {
    const { variant, subtext } = toast
    const isAbandoned = variant === 'abandoned'
    const isPixPaymentReminder = variant === 'pixPaymentReminder'

    return (
      <Button
        className={`flex h-[32px] ${isAbandoned ? 'relative' : ''} ${
          subtext ? 'absolute top-1 right-1' : ''
        }`}
        icon={
          isAbandoned || isPixPaymentReminder ? (
            <Icon
              name="CloseToastAbandoned"
              width={24}
              height={24}
              className="text-white"
            />
          ) : (
            <Icon name="CloseToast" width={32} height={32} />
          )
        }
        iconPosition="right"
        onClick={() => removeToast(toast)}
      />
    )
  }

  const { regularToastNodes, reminderToastNodes } = visibleToasts.reduce<{
    regularToastNodes: ReactNode[]
    reminderToastNodes: ReactNode[]
  }>(
    (acc, toast, idx) => {
      const { text, variant, image, subtext } = toast

      const toastNode = (
        <div
          key={`${variant}-${idx}`}
          id={
            variant === 'abandoned'
              ? `abandoned-cart-toast-${idx}`
              : `toast-${variant}-${idx}`
          }
          data-testid={`toast-${variant}`}
          className={getContainerClass(variant)}
        >
          <div className="flex items-center gap-4">
            {variant !== 'pixPaymentReminder' &&
              renderIconOrImage(variant, image, subtext)}
            <div className="flex flex-col">
              {text && (
                <span className={getTextClass(variant, !!subtext)}>{text}</span>
              )}

              {subtext &&
                (typeof subtext === 'string' ? (
                  <div className="mt-1 text-sm line-clamp-2 w-auto">
                    {subtext}
                  </div>
                ) : (
                  subtext
                ))}
            </div>
          </div>
          {renderCloseButton(toast)}
        </div>
      )

      if (isReminderToast(variant)) {
        acc.reminderToastNodes.push(toastNode)
      } else {
        acc.regularToastNodes.push(toastNode)
      }

      return acc
    },
    { regularToastNodes: [], reminderToastNodes: [] }
  )

  return (
    <>
      <div
        className="flex flex-col z-[9999] w-full gap-2 fixed top-0 sm:top-[11rem] sm:left-[65%] restructure-small-desktop:w-[375px]"
        style={{ top: isMobile ? 0 : `${toastTop}px` }}
      >
        {regularToastNodes}
      </div>
      {reminderToastNodes.length > 0 && (
        <div
          className="fixed items-end z-[9999] w-[92%] right-4 flex flex-col gap-2 restructure-tablet:left-0 restructure-tablet:right-0 restructure-tablet:mx-auto sm:w-[349px] restructure-small-desktop:left-[unset] restructure-small-desktop:right-4 restructure-small-desktop:mx-0"
          style={
            !isMobile
              ? { top: `${reminderTopValue}px`, transition: 'top 0.3s ease' }
              : {}
          }
        >
          {reminderToastNodes}
        </div>
      )}
    </>
  )
}
